import mixpanel from 'mixpanel-browser';
import { version as packageVersion } from '../../package.json';
import { env } from './env';
import { Sentry } from './sentry';

type PropValue = string | number | boolean | string[] | null | undefined;
export type AnalyticsProps = Record<string, PropValue>;

interface TrackOptions {
  // Same id + same time on a resend lets Mixpanel drop the duplicate.
  dedupe?: { id: string; time: Date | number | string };
  // For events fired right before a full-page redirect.
  transport?: 'sendBeacon';
}

// Mixpanel accepts `[A-Za-z0-9-]{1,36}`; the unique part of an id is its tail.
const toInsertId = (id: string) => id.replace(/[^A-Za-z0-9-]/g, '-').slice(-36);
// The SDK retries these itself; only drops and disabled queues are worth an alert.
const SELF_HEALING = /retry in|Network timeout; retrying/;

// The build's commit, so events can be split by release; the package version never changes.
const appVersion = import.meta.env.VITE_APP_VERSION || packageVersion;
const GLOBALS = { platform: 'web', app_version: appVersion, schema_version: 1 };

let enabled = false;
// Cleared on reset, so the next user is identified and snapshotted from scratch.
let identifiedUserId: string | null = null;
let lastSnapshot: AnalyticsProps = {};

export function reportAnalyticsError(msg: unknown, err?: unknown) {
  const text = msg instanceof Error ? msg.message : String(msg);
  if (SELF_HEALING.test(text)) return;
  Sentry.captureMessage(`analytics: ${text}`, {
    level: 'warning',
    tags: { analytics: 'mixpanel' },
    extra: { err },
  });
}

function captureFailure(err: unknown, event: string) {
  Sentry.captureException(err, { tags: { analytics: 'mixpanel', analytics_event: event } });
}

export function initAnalytics(token = env.ENABLE_ANALYTICS ? env.MIXPANEL_TOKEN : '') {
  enabled = false;
  identifiedUserId = null;
  lastSnapshot = {};
  if (!token) return;
  try {
    mixpanel.init(token, {
      autocapture: false,
      track_pageview: false,
      persistence: 'localStorage',
      error_reporter: reportAnalyticsError,
    });
    mixpanel.register(GLOBALS);
    enabled = true;
  } catch (err) {
    captureFailure(err, 'init');
  }
}

export function track(event: string, props: AnalyticsProps = {}, options: TrackOptions = {}) {
  if (!enabled) return;
  try {
    const { dedupe, transport } = options;
    const payload: Record<string, unknown> = { ...props };
    if (dedupe) {
      payload.$insert_id = toInsertId(dedupe.id);
      // Unix seconds, the browser SDK's own convention for `time`.
      payload.time = Math.floor(new Date(dedupe.time).getTime() / 1000);
    }
    if (transport) mixpanel.track(event, payload, { transport, send_immediately: true });
    else mixpanel.track(event, payload);
  } catch (err) {
    captureFailure(err, event);
  }
}

export function trackPageView(page: string) {
  track('page_viewed', { page });
}

export function identifyUser(user: {
  id: string;
  email?: string | null;
  name?: string | null;
  createdAt?: Date | string;
}) {
  if (!enabled || identifiedUserId === user.id) return;
  try {
    // Mixpanel persists the last identity; never carry it over to another user.
    const persistedUserId = mixpanel.get_property('$user_id');
    if (persistedUserId && persistedUserId !== user.id) resetAnalytics();
    mixpanel.identify(user.id);
    identifiedUserId = user.id;
    mixpanel.people.set({
      $email: user.email,
      $name: user.name,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(user.createdAt && { signup_date: new Date(user.createdAt).toISOString().slice(0, 10) }),
    });
    Sentry.setUser({ id: user.id });
  } catch (err) {
    captureFailure(err, 'identify');
  }
}

// Profile only: read in reports through the user, e.g. tone_attributes.
export function setUserProperties(props: AnalyticsProps) {
  if (!enabled) return;
  try {
    mixpanel.people.set(props);
  } catch (err) {
    captureFailure(err, 'set_user_properties');
  }
}

// Profile plus every later event, so reports see the value at the time of the event.
export function setSnapshot(props: AnalyticsProps) {
  if (!enabled) return;
  const changed = Object.fromEntries(
    Object.entries(props).filter(([key, value]) => lastSnapshot[key] !== value)
  );
  if (Object.keys(changed).length === 0) return;
  try {
    mixpanel.people.set(changed);
    mixpanel.register(changed);
    lastSnapshot = { ...lastSnapshot, ...changed };
  } catch (err) {
    captureFailure(err, 'set_snapshot');
  }
}

// For a signed-out visit: drops an identity a lapsed session left behind, but keeps
// an anonymous visitor's id so their pre-signup events stay linked.
export function clearStaleIdentity() {
  if (!enabled) return;
  try {
    if (identifiedUserId || mixpanel.get_property('$user_id')) resetAnalytics();
  } catch (err) {
    captureFailure(err, 'reset');
  }
}

export function resetAnalytics() {
  identifiedUserId = null;
  lastSnapshot = {};
  Sentry.setUser(null);
  if (!enabled) return;
  try {
    mixpanel.reset();
    // reset() also drops the super properties.
    mixpanel.register(GLOBALS);
  } catch (err) {
    captureFailure(err, 'reset');
  }
}
